import React, { useMemo } from 'react';

import {
  Alert,
  ButtonBase,
  CircularProgress,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from '@mui/material';
import { alpha } from '@mui/material/styles';

import type {
  CorridorCalendarGridCell,
  CorridorCalendarGridResult,
  CorridorPriceValidation,
} from '~/types/corridorOpportunity';

function aud(value: number): string {
  return `A$${Math.round(value).toLocaleString('en-AU')}`;
}

function formatGridDate(value: string): string {
  return new Intl.DateTimeFormat('en-AU', {
    day: 'numeric',
    month: 'short',
    weekday: 'short',
  }).format(new Date(`${value}T00:00:00`));
}

function adultFare(cell: CorridorCalendarGridCell): number {
  return cell.priceBreakdown.adult?.totalFare ?? cell.totalPrice;
}

interface Props {
  grid?: CorridorCalendarGridResult;
  selectedDepartureDate: string;
  selectedReturnDate: string;
  checkedFare: CorridorPriceValidation | null;
  checkedFareIsFresh: boolean;
  isLoading: boolean;
  errorMessage?: string;
  onSelect: (departureDate: string, returnDate: string, carrier: string) => void;
}

export default function FareCalendarGrid({
  grid,
  selectedDepartureDate,
  selectedReturnDate,
  checkedFare,
  checkedFareIsFresh,
  isLoading,
  errorMessage,
  onSelect,
}: Props) {
  const lowestFare = useMemo(() => {
    const fares = (grid?.rows ?? []).flatMap((row) => row.cells.flatMap((cell) => (cell ? [adultFare(cell)] : [])));
    return fares.length ? Math.min(...fares) : null;
  }, [grid]);

  if (isLoading) {
    return (
      <Stack alignItems="center" gap={1.5} sx={{ py: 6 }}>
        <CircularProgress size={28} />
        <Typography variant="body2" color="text.secondary">
          Loading the calendar grid…
        </Typography>
      </Stack>
    );
  }

  if (errorMessage) {
    return <Alert severity="error">The calendar grid could not be loaded. {errorMessage}</Alert>;
  }

  if (!grid) {
    return null;
  }

  if (!grid.departureDates.length || !grid.rows.some((row) => row.cells.some(Boolean))) {
    return (
      <Alert severity="info">
        No indicative fares were found around these dates. Change the calendar-grid centre or remove the carrier filter
        and try again.
      </Alert>
    );
  }

  return (
    <Stack gap={1}>
      <TableContainer
        sx={{
          border: '1px solid',
          borderColor: 'divider',
          borderRadius: 2,
          maxHeight: 560,
        }}
      >
        <Table stickyHeader size="small" aria-label="Fare calendar grid" sx={{ minWidth: 820 }}>
          <TableHead>
            <TableRow>
              <TableCell
                sx={{
                  bgcolor: 'grey.100',
                  left: 0,
                  minWidth: 122,
                  position: 'sticky',
                  zIndex: 3,
                }}
              >
                <Typography variant="caption" color="text.secondary" display="block">
                  Return ↓
                </Typography>
                <Typography variant="caption" fontWeight={700}>
                  Depart →
                </Typography>
              </TableCell>
              {grid.departureDates.map((date) => (
                <TableCell key={date} align="center" sx={{ bgcolor: 'grey.100', minWidth: 112 }}>
                  <Typography variant="caption" color="text.secondary" display="block">
                    Outbound
                  </Typography>
                  <Typography variant="body2" fontWeight={700} noWrap>
                    {formatGridDate(date)}
                  </Typography>
                </TableCell>
              ))}
            </TableRow>
          </TableHead>
          <TableBody>
            {grid.rows.map((row) => (
              <TableRow key={row.returnDate ?? 'one-way'}>
                <TableCell
                  component="th"
                  scope="row"
                  sx={{
                    bgcolor: 'background.paper',
                    left: 0,
                    minWidth: 122,
                    position: 'sticky',
                    zIndex: 2,
                  }}
                >
                  <Typography variant="caption" color="text.secondary" display="block">
                    Return
                  </Typography>
                  <Typography variant="body2" fontWeight={700} noWrap>
                    {row.returnDate ? formatGridDate(row.returnDate) : 'One way'}
                  </Typography>
                </TableCell>
                {grid.departureDates.map((departureDate, columnIndex) => {
                  const cell = row.cells[columnIndex];
                  const returnDate = row.returnDate;
                  const fare = cell ? adultFare(cell) : null;
                  const isSelected = departureDate === selectedDepartureDate && returnDate === selectedReturnDate;
                  const isChecked =
                    !!checkedFare &&
                    checkedFare.departureDate === departureDate &&
                    checkedFare.returnDate === returnDate &&
                    checkedFare.carrier?.toUpperCase() === cell?.carrier.toUpperCase();
                  const isLowest = fare !== null && fare === lowestFare;

                  return (
                    <TableCell key={`${returnDate}-${departureDate}`} padding="none" align="center">
                      {cell && returnDate ? (
                        <ButtonBase
                          aria-label={`Select outbound ${formatGridDate(departureDate)} and return ${formatGridDate(
                            returnDate,
                          )}, ${aud(fare)} per adult, ${cell.carrier}`}
                          aria-pressed={isSelected}
                          onClick={() => onSelect(departureDate, returnDate, cell.carrier)}
                          sx={(theme) => ({
                            alignItems: 'stretch',
                            bgcolor: isSelected
                              ? alpha(theme.palette.primary.main, 0.1)
                              : isLowest
                                ? alpha(theme.palette.success.main, 0.1)
                                : theme.palette.background.paper,
                            border: '2px solid',
                            borderColor: isSelected ? 'primary.main' : 'transparent',
                            display: 'flex',
                            flexDirection: 'column',
                            justifyContent: 'center',
                            minHeight: 84,
                            p: 1,
                            width: '100%',
                            '&:hover': {
                              bgcolor: isSelected
                                ? alpha(theme.palette.primary.main, 0.16)
                                : theme.palette.action.hover,
                            },
                            '&:focus-visible': {
                              outline: '3px solid',
                              outlineColor: 'primary.light',
                              outlineOffset: -3,
                            },
                          })}
                        >
                          <Typography fontWeight={700}>{aud(fare)}</Typography>
                          <Typography variant="caption" color="text.secondary">
                            {cell.carrier}
                          </Typography>
                          {(isLowest || isChecked) && (
                            <Typography
                              variant="caption"
                              color={
                                isChecked ? (checkedFareIsFresh ? 'primary.main' : 'warning.main') : 'success.main'
                              }
                              fontWeight={700}
                            >
                              {isChecked ? (checkedFareIsFresh ? 'Live checked' : 'Check expired') : 'Lowest in grid'}
                            </Typography>
                          )}
                        </ButtonBase>
                      ) : (
                        <Typography color="text.disabled" aria-label="No fare found">
                          —
                        </Typography>
                      )}
                    </TableCell>
                  );
                })}
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
      <Typography variant="caption" color="text.secondary">
        Prices are indicative calendar-search fares for one adult. Select a date pair, then check the live fare before
        using it in a campaign.
      </Typography>
    </Stack>
  );
}
